import DashboardStatisticsAllPeriod from './DashboardStatisticsAllPeriod';
import DashboardCrMutuiLeadChart from './DashboardCrMutuiLeadChart';
import DashboardCrTotalLeadsChart from './DashboardCrTotalLeadsChart';
import DashboardRevenuePerLeadChart from './DashboardRevenuePerLeadChart';
import DashboardFunnelPerStage from './DashboardFunnelPerStage';
import {
  BenchmarkStatistics,
  DroppedReason,
  FunnelPerStageStatistics,
  MonthlyStatistics,
  Statistics,
} from '@tassomutuo-backoffice/types';
import { useState } from 'react';
import DashboardReceivedLeadsChart from './DashboardReceivedLeadsChart';
import DashboardEstimatedMortgagesChart from './DashboardEstimatedMortgagesChart';
import DashboardEstimatedRevenueChart from './DashboardEstimatedRevenueChart';
import DashboardEffectiveRevenueChart from './DashboardEffectiveRevenueChart';
import { Box, Typography } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import DashboardLegendCardModal from './DashboardLegendCardModal';
import DashboardLostRevenueChart from './DashboardLostRevenueChart';
import DashboardDropReasonsSummary from './DashboardDropReasonsSummary';

export type CardOptions =
  | 'receivedLeads'
  | 'estimatedMortgages'
  | 'conversionRate'
  | 'conversionRateTotalLeads'
  | 'estimatedRevenue'
  | 'revenuePerLead'
  | 'effectiveRevenue'
  | 'lostRevenue';

interface DashboardContentProps {
  isTeamStatistics?: boolean;
  statisticsAllPeriod?: Statistics;
  statisticsAllPeriodTrend?: Statistics;
  statisticsPerMonth?: MonthlyStatistics;
  funnelPerStageStatistics?: FunnelPerStageStatistics;
  benchmarkStatistics?: BenchmarkStatistics;
  showAllCharts?: boolean;
  isPrinting?: boolean;
  droppedReasons?: DroppedReason;
}

const DashboardContent = ({
  isTeamStatistics,
  statisticsAllPeriod,
  statisticsPerMonth,
  funnelPerStageStatistics,
  benchmarkStatistics,
  statisticsAllPeriodTrend,
  showAllCharts,
  isPrinting,
  droppedReasons,
}: DashboardContentProps) => {
  const [showModalCardsLegend, setShowModalCardsLegend] = useState(false);
  const [selectedCard, setSelectedCard] =
    useState<CardOptions>('receivedLeads');

  // vado a calcolare il funnelPerStageStatistics del benchmark sommando tutti i mesi
  const benchmarkFunnelPerStageStatistics =
    benchmarkStatistics?.benchmarkStatisticsPerMonth.reduce(
      (acc, monthStats) => {
        const monthFunnelPerStage = monthStats.funnelPerStageStatistics;

        acc.totalReceivedLeads += monthFunnelPerStage.totalReceivedLeads;
        acc.confirmedAppointments += monthFunnelPerStage.confirmedAppointments;
        acc.meetingDone += monthFunnelPerStage.meetingDone;
        acc.inDevelopment += monthFunnelPerStage.inDevelopment;
        acc.advancedStage += monthFunnelPerStage.advancedStage;
        acc.concludedNotarialActs += monthFunnelPerStage.concludedNotarialActs;
        acc.dropped += monthFunnelPerStage.dropped;

        return acc;
      },
      {
        totalReceivedLeads: 0,
        confirmedAppointments: 0,
        meetingDone: 0,
        inDevelopment: 0,
        advancedStage: 0,
        concludedNotarialActs: 0,
        dropped: 0,
      }
    );

  return (
    <>
      {isTeamStatistics !== undefined && !isPrinting && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6">
            {isTeamStatistics
              ? 'Clicca su una card per analizzare l’andamento del lavoro dei tuoi consulenti'
              : 'Clicca su una card per analizzare l’andamento del tuo lavoro'}
          </Typography>

          <Box
            sx={{
              bgcolor: (th) => th.palette.primary.light,
              borderRadius: '5px',
              width: '30px',
              height: '30px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => setShowModalCardsLegend(true)}
          >
            <PriorityHighIcon fontSize="small" sx={{ color: 'white' }} />
          </Box>
        </Box>
      )}
      {statisticsAllPeriod && (
        <Box>
          <DashboardStatisticsAllPeriod
            statistics={statisticsAllPeriod}
            statisticsTrend={statisticsAllPeriodTrend}
            selectedCard={selectedCard}
            onChangeCard={setSelectedCard}
            isPrinting={isPrinting}
          />
        </Box>
      )}

      {statisticsPerMonth && (
        <>
          {(selectedCard === 'receivedLeads' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardReceivedLeadsChart
                statisticsPerMonth={statisticsPerMonth}
                isPrinting={isPrinting}
              />
              {!isPrinting && (
                <DashboardLostRevenueChart
                  statisticsPerMonth={statisticsPerMonth}
                  isPrinting={isPrinting}
                />
              )}
            </Box>
          )}
          {(selectedCard === 'estimatedMortgages' || showAllCharts) && (
            <Box>
              <DashboardEstimatedMortgagesChart
                statisticsPerMonth={statisticsPerMonth}
                benchmarkStatisticsPerMonth={
                  benchmarkStatistics?.benchmarkStatisticsPerMonth
                }
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {(selectedCard === 'conversionRate' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardCrMutuiLeadChart
                statisticsPerMonth={statisticsPerMonth}
                benchmarkStatisticsPerMonth={
                  benchmarkStatistics?.benchmarkStatisticsPerMonth
                }
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {(selectedCard === 'conversionRateTotalLeads' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardCrTotalLeadsChart
                statisticsPerMonth={statisticsPerMonth}
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {(selectedCard === 'revenuePerLead' || showAllCharts) && (
            <Box>
              <DashboardRevenuePerLeadChart
                statisticsPerMonth={statisticsPerMonth}
                benchmarkStatisticsPerMonth={
                  benchmarkStatistics?.benchmarkStatisticsPerMonth
                }
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {(selectedCard === 'estimatedRevenue' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardEstimatedRevenueChart
                statisticsPerMonth={statisticsPerMonth}
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {(selectedCard === 'effectiveRevenue' || showAllCharts) && (
            <Box>
              <DashboardEffectiveRevenueChart
                statisticsPerMonth={statisticsPerMonth}
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {(selectedCard === 'lostRevenue' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardLostRevenueChart
                statisticsPerMonth={statisticsPerMonth}
                isPrinting={isPrinting}
              />
            </Box>
          )}
          {/* <DashboardStaticsPerMonths statisticsPerMonth={statisticsPerMonth} />*/}
        </>
      )}

      {funnelPerStageStatistics && (
        <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
          <DashboardFunnelPerStage
            funnelPerStageStatistics={funnelPerStageStatistics}
            benchmarkFunnelPerStageStatistics={
              benchmarkFunnelPerStageStatistics
            }
            isPrinting={isPrinting}
          />
        </Box>
      )}

      {isTeamStatistics && (
        <DashboardDropReasonsSummary
          isPrinting={isPrinting}
          droppedReasons={droppedReasons}
          benchmarkDroppedReasons={
            benchmarkStatistics?.benchmarkStatisticsAllPeriod.droppedReasons
          }
        />
      )}
      {showModalCardsLegend && (
        <DashboardLegendCardModal
          open
          onClose={() => setShowModalCardsLegend(false)}
        />
      )}
    </>
  );
};

export default DashboardContent;
